// src/eventsettings/eventsettings.jsx
// イベント設定（管理者・主催者で共通）：審査員・リング・担当グループ・開始時刻を入力して保存する。
// basePath は、一覧に戻るリンクの行き先を決める（"/admin" または "/operator"）。
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import EventPdfSettings from "./eventpdfs";
import "./eventsettings.css";

const API_URL = import.meta.env.VITE_API_URL;

const RINGS = [1, 2, 3, 4, 5];

// 画面用の行を作る。groups は { [breed_group_id]: "HH:MM" | "" } （キーがあれば担当）
let rowSeq = 0;
const newRow = (init = {}) => ({
  key: `row-${rowSeq++}`,
  judge_name: init.judge_name ?? "",
  ring_no: init.ring_no ?? "",
  special_role: init.special_role ?? "",
  booklet_order: init.booklet_order ?? "",
  groups: init.groups ?? {},
});

const toRows = (assignments) =>
  assignments.map((a) =>
    newRow({
      judge_name: a.judge_name,
      ring_no: a.ring_no,
      special_role: a.special_role,
      booklet_order: a.booklet_order,
      groups: Object.fromEntries(
        a.groups.map((g) => [g.breed_group_id, g.start_time || ""])
      ),
    })
  );

// 未保存の変更の判定用：画面の行と保存済みのデータを、同じ形の文字列にそろえて比べる
// （n=氏名 / r=リング / s=役割 / b=冊子の表示順 / g=担当グループと開始時刻）
const byId = (a, b) => a[0] - b[0];
const rowsSnapshot = (rows) =>
  JSON.stringify(
    rows.map((r) => ({
      n: r.judge_name.trim(),
      r: r.ring_no === "" ? null : Number(r.ring_no),
      s: r.special_role || "",
      b: r.booklet_order === "" ? null : Number(r.booklet_order),
      g:
        r.ring_no === ""
          ? []
          : Object.entries(r.groups).map(([id, t]) => [Number(id), t || ""]).sort(byId),
    }))
  );
const savedSnapshot = (assignments) =>
  JSON.stringify(
    assignments.map((a) => ({
      n: a.judge_name,
      r: a.ring_no ?? null,
      s: a.special_role || "",
      b: a.booklet_order ?? null,
      g: a.groups.map((g) => [g.breed_group_id, g.start_time || ""]).sort(byId),
    }))
  );

/* ========================================
 * コンポーネント
 * ======================================== */
export default function EventSettings({ basePath }) {
  const { eventId } = useParams();
  const queryClient = useQueryClient();
  const queryKey = ["event-settings", eventId];

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/events/${eventId}/settings/`);
      if (!res.data?.success) throw new Error("イベント設定の取得に失敗しました");
      return res.data.data;
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState(null); // { type: "ok" | "error", text }
  const [tab, setTab] = useState("settings"); // "settings" | "pdf"
  const [editing, setEditing] = useState(false); // 編集中か（初期は閲覧のみ）

  // 取得できたら、入力用の行に展開する。編集中は、入力中の内容を消さないよう、作り直さない
  useEffect(() => {
    if (data && !editing) setRows(toRows(data.assignments));
    // editing の変化では作り直さない（キャンセル時は、cancelEdit が戻す）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // 未保存の変更があるか（編集中で、画面の内容が保存済みの内容と違う）
  const dirty = !!data && editing && rowsSnapshot(rows) !== savedSnapshot(data.assignments);

  // 未保存のまま、タブを閉じる・再読み込みをするときは、ブラウザの確認を出す
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const save = useMutation({
    mutationFn: async (assignments) => {
      const res = await axios.put(
        `${API_URL}/api/events/${eventId}/settings/`,
        { assignments }
      );
      return res.data.data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      setMessage({ type: "ok", text: "保存しました" });
      setEditing(false); // 保存したら、閲覧に戻る
    },
    onError: (err) => {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || "保存に失敗しました",
      });
    },
  });

  if (isLoading) return <div className="evSetPage evSetLoading">読み込み中...</div>;
  if (isError)
    return (
      <div className="evSetPage">
        <Link to={`${basePath}/eventlist`} className="evSetBack">
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
          戻る
        </Link>
        <div className="evSetMsg isError">
          {error?.response?.status === 404
            ? "イベントが見つかりません"
            : `エラー: ${error?.message || "取得に失敗しました"}`}
        </div>
      </div>
    );

  const { event, groups } = data;

  const updateRow = (key, patch) => {
    setMessage(null);
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const toggleGroup = (row, breedGroupId, checked) => {
    const next = { ...row.groups };
    if (checked) next[breedGroupId] = next[breedGroupId] ?? "";
    else delete next[breedGroupId];
    updateRow(row.key, { groups: next });
  };

  const setGroupTime = (row, breedGroupId, time) =>
    updateRow(row.key, { groups: { ...row.groups, [breedGroupId]: time } });

  const removeRow = (key) => {
    setMessage(null);
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const startEdit = () => {
    setMessage(null);
    setEditing(true);
  };

  // 変更を捨てて、保存済みの内容に戻す（追加・削除・入力の変更を、まとめて取り消す）
  const cancelEdit = () => {
    if (dirty && !window.confirm("変更を破棄して、編集を終わりますか？")) return;
    setRows(toRows(data.assignments));
    setMessage(null);
    setEditing(false);
  };

  // 「戻る」リンク：未保存の変更があるときは、確認する
  const confirmLeave = (e) => {
    if (dirty && !window.confirm("未保存の変更があります。破棄して戻りますか？")) e.preventDefault();
  };

  const handleSave = () => {
    if (rows.some((r) => r.judge_name.trim() === "")) {
      setMessage({ type: "error", text: "審査員の氏名を入力してください" });
      return;
    }
    setMessage(null);
    save.mutate(
      rows.map((r) => ({
        judge_name: r.judge_name.trim(),
        // リングを担当しない人（チーフスチュワードなど）は null。担当グループも付けない
        ring_no: r.ring_no === "" ? null : Number(r.ring_no),
        special_role: r.special_role,
        booklet_order: r.booklet_order === "" ? null : Number(r.booklet_order),
        groups:
          r.ring_no === ""
            ? []
            : Object.entries(r.groups).map(([id, time]) => ({
                breed_group_id: Number(id),
                start_time: time || null,
              })),
      }))
    );
  };

  return (
    <div className="evSetPage">
      <Link to={`${basePath}/eventlist`} className="evSetBack" onClick={confirmLeave}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6 6 6" />
        </svg>
        戻る
      </Link>
      <p className="evSetSub">
        {event.title}
        {event.date ? `（${event.date}）` : ""}
      </p>

      <div className="evSetTabs" role="tablist" aria-label="設定の種類">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "settings"}
          className="evSetTab"
          onClick={() => setTab("settings")}
        >
          イベント設定{dirty && <span className="evSetTabDirty" aria-label="未保存の変更あり"> ●</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pdf"}
          className="evSetTab"
          onClick={() => setTab("pdf")}
        >
          PDF設定
        </button>
      </div>

      {tab === "settings" && (
      <>
      <div className="evSetEditBar">
        {editing ? (
          <>
            <span className="evSetEditState">
              <span className="evSetEditTag">編集中</span>
              {dirty && <span className="evSetDirty">未保存の変更があります</span>}
            </span>
            <span className="evSetEditBtns">
              {message && (
                <span className={`evSetMsg ${message.type === "ok" ? "isOk" : "isError"}`}>
                  {message.text}
                </span>
              )}
              <button type="button" className="evSetBtn" disabled={save.isPending} onClick={cancelEdit}>
                キャンセル
              </button>
              <button
                type="button"
                className="evSetBtn isPrimary"
                disabled={save.isPending}
                onClick={handleSave}
              >
                {save.isPending ? "保存中..." : "保存"}
              </button>
            </span>
          </>
        ) : (
          <>
            <span className={message?.type === "ok" ? "evSetMsg isOk" : "evSetPdfMeta"}>
              {message?.type === "ok" ? message.text : "内容を変更するには「編集」を押してください"}
            </span>
            <span className="evSetEditBtns">
              <button type="button" className="evSetBtn isPrimary" onClick={startEdit}>
                編集
              </button>
            </span>
          </>
        )}
      </div>

      {rows.length === 0 && !editing && (
        <div className="evSetPdfMeta">審査員が登録されていません。「編集」から追加してください。</div>
      )}

      {rows.map((row, index) => {
        // 他のカードが担当中のグループ
        const takenGroups = new Set(
          rows.filter((r) => r.key !== row.key).flatMap((r) => Object.keys(r.groups).map(Number))
        );

        return (
          <div key={row.key} className="evSetCard">
            <div className="evSetCardHead">
              <span>審査員 {index + 1}</span>
              {editing && (
                <button
                  type="button"
                  className="evSetBtn isDanger"
                  onClick={() => removeRow(row.key)}
                >
                  削除
                </button>
              )}
            </div>

            <div className="evSetFields">
              <label className="evSetField">
                審査員の氏名
                <input
                  type="text"
                  maxLength={100}
                  value={row.judge_name}
                  disabled={!editing}
                  onChange={(e) => updateRow(row.key, { judge_name: e.target.value })}
                />
              </label>

              <label className="evSetField">
                リング
                <select
                  value={row.ring_no}
                  disabled={!editing}
                  onChange={(e) =>
                    updateRow(
                      row.key,
                      e.target.value === ""
                        ? { ring_no: "", groups: {} }
                        : { ring_no: e.target.value }
                    )
                  }
                >
                  <option value="">リングなし</option>
                  {RINGS.map((n) => (
                    <option key={n} value={n}>
                      リング {n}
                    </option>
                  ))}
                </select>
              </label>

              <label className="evSetField">
                役割（任意）
                <input
                  type="text"
                  maxLength={100}
                  value={row.special_role}
                  disabled={!editing}
                  placeholder="例：ベストインショージャッジ、BPIS"
                  onChange={(e) => updateRow(row.key, { special_role: e.target.value })}
                />
              </label>

              <label className="evSetField">
                冊子の表示順
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={row.booklet_order}
                  disabled={!editing}
                  placeholder="空白は表示なし"
                  onChange={(e) => updateRow(row.key, { booklet_order: e.target.value })}
                />
              </label>
            </div>

            {row.ring_no !== "" && (
            <>
            <div className="evSetGroupsLabel">担当グループと開始時刻</div>
            <div className="evSetGroups">
              {groups.map((g) => {
                const checked = g.breed_group_id in row.groups;
                const taken = takenGroups.has(g.breed_group_id);
                return (
                  <div
                    key={g.breed_group_id}
                    className={`evSetGroup ${taken ? "isTaken" : ""}`}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={taken || !editing}
                        onChange={(e) =>
                          toggleGroup(row, g.breed_group_id, e.target.checked)
                        }
                      />
                      {g.name}
                    </label>
                    {checked && (
                      <input
                        type="time"
                        aria-label={`${g.name} の開始時刻`}
                        value={row.groups[g.breed_group_id]}
                        disabled={!editing}
                        onChange={(e) =>
                          setGroupTime(row, g.breed_group_id, e.target.value)
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>
        );
      })}

      {editing && (
        <div className="evSetActions">
          <button
            type="button"
            className="evSetBtn"
            onClick={() => {
              setMessage(null);
              setRows((prev) => [...prev, newRow()]);
            }}
          >
            ＋ 審査員を追加
          </button>
        </div>
      )}
      </>
      )}

      {tab === "pdf" && <EventPdfSettings eventId={eventId} basePath={basePath} dirty={dirty} />}
    </div>
  );
}
